import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import {
  selectJourneyContext,
  selectHasSkipped,
  selectCurrentStep,
  completeStep,
} from 'features/onboarding/reducers/onboardingSlice';
import { useJourneyContext } from 'features/onboarding/hooks/useJourneyContext';
import SnowflakeSetupStep from 'features/onboarding/components/SnowflakeSetupStep';
import RunnerProvisionStep from 'features/onboarding/components/RunnerProvisionStep';
import LoadingView from './LoadingView';
import { signupApi } from 'features/frictionless-signup/services/signupApi';

/**
 * OnboardingView Component
 *
 * Displays the appropriate onboarding step based on the current Redux state.
 * The step automatically progresses when completeStep is dispatched.
 *
 * Step progression:
 * - snowflake_setup -> runner_provision (via completeStep('snowflake_setup'))
 * - runner_provision -> complete (via completeStep('runner_provision'))
 * - complete -> redirect to dashboard
 */
const OnboardingView: React.FC = () => {
  const dispatch = useAppDispatch();
  const journeyContext = useAppSelector(selectJourneyContext);
  const hasSkipped = useAppSelector(selectHasSkipped);
  const currentStep = useAppSelector(selectCurrentStep);
  const posthog = usePostHog();

  // Parse journey from URL on mount (for legacy/direct navigation)
  const parsedJourney = useJourneyContext();

  // Check for existing Snowflake account and skip setup if already connected
  useEffect(() => {
    const checkExistingProgress = async (): Promise<void> => {
      try {
        const status = await signupApi.getCurrentUserSignupStatus();

        if (status !== null) {
          // If user has a Snowflake account, skip to runner provisioning
          if (status.has_snowflake_account && currentStep === 'snowflake_setup') {
            console.log('[OnboardingView] Existing Snowflake account detected, skipping to runner provision');
            dispatch(completeStep('snowflake_setup'));
          }
          // If user has both Snowflake account and runner, complete onboarding
          else if (status.has_snowflake_account && status.has_runner && currentStep === 'runner_provision') {
            console.log('[OnboardingView] Snowflake account and runner already exist, completing onboarding');
            dispatch(completeStep('runner_provision'));
          }
        }
      } catch (error) {
        console.error('[OnboardingView] Failed to check existing progress:', error);
        // On error, stay on current step (fail gracefully)
      }
    };

    void checkExistingProgress();
  }, [dispatch, currentStep]);

  // Show loading while journey context is being set
  if (journeyContext == null && parsedJourney != null) {
    return <LoadingView />;
  }

  // If user has skipped onboarding, redirect to dashboard
  if (hasSkipped) {
    const redirectTo = journeyContext?.redirect_to ?? '/';
    return <Navigate to={redirectTo} replace />;
  }

  // If onboarding is complete, redirect to journey-specific destination or dashboard
  if (currentStep === 'complete') {
    const redirectTo = journeyContext?.redirect_to ?? '/';
    console.log('[OnboardingView] Onboarding complete, redirecting to:', redirectTo);

    // Track onboarding completion
    posthog?.capture('admin_app.frictionless.onboarding_completed', {
      app_name: journeyContext?.app_name ?? 'DataOps.live',
      app_variant: journeyContext?.app_variant ?? 'default',
      redirect_to: redirectTo,
    });

    return <Navigate to={redirectTo} replace />;
  }

  // Route to appropriate step based on Redux state
  switch (currentStep) {
    case 'snowflake_setup':
      return <SnowflakeSetupStep />;
    case 'runner_provision':
      return <RunnerProvisionStep />;
    default:
      // Fallback to dashboard if unknown step
      console.warn('[OnboardingView] Unknown step:', currentStep);
      return <Navigate to="/" replace />;
  }
};

export default OnboardingView;
